import { makeAutoObservable } from 'mobx';
import { Result } from 'neverthrow';

import { IDataStore } from 'app/common/data-storage/features';
import { INotifications } from 'app/common/notifications/core/notifications';
import { HttpRequest, IHttpClient } from 'app/libs/http-client';
import { Fail } from 'app/libs/http-client/fails/fail';
import { ObservableRequest } from 'app/libs/observable-request';

import { isAuthRequired } from '../api/error-guards';

export interface IHttpResource<Params> {
  get isLoading(): boolean;
  get hasError(): boolean;
  get error(): string;

  send(params: Params): void;
  reset(): void;
}

/*
 1. Если нужен сайд-эффект или какая-то реакция, используй параметр onFinish(enttity: Entity): void
 Например, можно сделать редирект с помощью роутера на созаднную сущность.

 2. Для сохранение данных во внешнее хранилище используй параметр dataStore;
 В него необходимо передать объект, которы реализует интерфейс IDataStorage<Entity>
*/

type FinishCallback<Params> = (result: Params) => void;
type ErrorCallback<Params> = (result: Params) => void;
type OptFnArg<Param, Result> = [Param] extends [void]
  ? () => Result
  : (arg: Param) => Result;

type HttpResourceResourceParams<Params, Done> = {
  apiClient: IHttpClient;
  httpRequest: OptFnArg<Params, HttpRequest>;
  errorNotify?: INotifications;
  onFinish?: FinishCallback<Done>;
  onError?: ErrorCallback<Fail>;
  onAuthRequired?: ErrorCallback<Fail>;
  dataStore?: IDataStore<Done>;
};

export class HttpResource<Params, Done> implements IHttpResource<Params> {
  private request: ObservableRequest<Params, Done>;
  private finishCallback?: FinishCallback<Done>;
  private errorCallback?: ErrorCallback<Fail>;
  private authErrorCallback?: ErrorCallback<Fail>;
  private dataStore?: IDataStore<Done>;
  private _error = '';
  private errorNotify?: INotifications;

  public get isLoading() {
    return this.request.isLoading;
  }

  public get hasError(): boolean {
    return !!this._error;
  }

  public get error(): string {
    return this._error;
  }

  constructor({
    apiClient,
    httpRequest,
    errorNotify,
    onFinish,
    onError,
    onAuthRequired,
    dataStore,
  }: HttpResourceResourceParams<Params, Done>) {
    const request = (params: Params) =>
      apiClient.fetch<Done>(httpRequest(params));

    this.request = new ObservableRequest<Params, Done>(
      request as OptFnArg<Params, Promise<Result<Done, Fail>>>,
      {
        done: (result) => this.handleFinish(result),
        fail: (error) => this.handleError(error),
      }
    );

    this.finishCallback = onFinish;
    this.errorCallback = onError;
    this.authErrorCallback = onAuthRequired;
    this.dataStore = dataStore;
    this.errorNotify = errorNotify;

    makeAutoObservable(this);
  }

  private handleFinish(result: Done) {
    this.dataStore && this.dataStore.setData(result);
    this.finishCallback && this.finishCallback(result);
  }

  private handleError(error: Fail) {
    if (isAuthRequired(error))
      return this.authErrorCallback && this.authErrorCallback(error);

    this.errorCallback && this.errorCallback(error);
    this.errorNotify &&
      this.errorNotify.addMessage({ type: 'error', message: error.message });
  }

  public send(params: Params) {
    this.request.fetch(params);
  }

  public reset(): void {
    this.request.reset();
    this._error = '';
  }
}
